/**
 * TransactionSamplePage — 다중 DB 트랜잭션 샘플.
 *
 * 【역할】
 * - PRIMARY(고객) + WAREHOUSE(재고) 이종 DB 연동 트랜잭션 데모
 * - 독립/원자적 저장 2가지 모드 비교
 *
 * 【주요 기능】
 * - 조회: 양 DB 동시 조회 (customers + inventory)
 * - Case 1: PRIMARY 단독 저장 (고객 insert)
 * - Case 2: WAREHOUSE 단독 저장 (재고 insert)
 * - Case 3: 독립 저장 (각 DB 별 커밋 — 부분 실패 허용)
 * - Case 4: 원자적 저장 (2 DB 전부 성공해야 커밋, 하나라도 실패 시 전체 롤백)
 *
 * 【사용 훅】
 * - useFramework ($transaction, loading)
 * - useDataSet / useLabel
 *
 * 【Backend RPC serviceName】
 * - txSample/searchBoth  — 양쪽 DB 조회
 * - txSample/save*       — 4가지 저장 케이스 (뒤에 SuffixCase1~4)
 *
 * 【DataSet 구조】
 * - customersDs: 고객 목록 (PRIMARY DB)
 * - inventoryDs: 재고 목록 (WAREHOUSE DB)
 *
 * 【사용처】 router/index.tsx — `{ path: 'tx-sample' }`
 * 【포팅 원본】 vue-spring-fw/frontend/src/pages/PageTransactionSample.vue
 */
import { useEffect, useState } from 'react'
import { Card, Flex, Button, Input, Space, Alert } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import PageContainer from '@/components/common/PageContainer'
import DataTable from '@/components/common/DataTable'
import { useFramework, useDataSet } from '@/framework'
import { useLabel } from '@/framework/useLabel'

interface Customer {
  customerId: number
  customerName: string
  customerType: string
}
interface Inventory {
  inventoryId: number
  itemName: string
  warehouseCd: string
  quantity: number
}

type Result = { ok: boolean; msg: string } | null

/**
 * TransactionSamplePage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function TransactionSamplePage() {
  const { $transaction, loading } = useFramework()
  const { t } = useLabel()
  const customersDs = useDataSet<Customer>([])
  const inventoryDs = useDataSet<Inventory>([])

  const [case1Name, setCase1Name] = useState('신규고객')
  const [case2Name, setCase2Name] = useState('신규품목')
  const [case3Name, setCase3Name] = useState('독립저장')
  const [case4Name, setCase4Name] = useState('원자적저장')

  const [case1Result, setCase1Result] = useState<Result>(null)
  const [case2Result, setCase2Result] = useState<Result>(null)
  const [case3Result, setCase3Result] = useState<Result>(null)
  const [case4Result, setCase4Result] = useState<Result>(null)

  const fn_refresh = async () => {
    await $transaction({
      id: 'refresh',
      service: 'txSample/searchBoth',
      action: 'search',
      outds: { ds_customer: customersDs, ds_inventory: inventoryDs }
    })
  }

  useEffect(() => {
    void fn_refresh()
  }, [])

  const runCase = async (
    caseId: string,
    name: string,
    rollback: boolean,
    setResult: (r: Result) => void
  ) => {
    const res = await $transaction({
      id: caseId,
      service: `txSample/${caseId}`,
      action: 'save',
      params: { name, rollback }
    })
    setResult({ ok: res.success, msg: res.message ?? (res.success ? 'OK' : 'FAIL') })
    await fn_refresh()
  }

  const cases: Array<{
    id: string
    title: string
    subtitle: string
    value: string
    setValue: (v: string) => void
    result: Result
  }> = [
    {
      id: 'case1PrimaryOnly',
      title: t('LBL_CASE1', 'Case 1: PRIMARY 단독'),
      subtitle: t('LBL_SUB_CASE1', '@Transactional'),
      value: case1Name,
      setValue: setCase1Name,
      result: case1Result
    },
    {
      id: 'case2WarehouseOnly',
      title: t('LBL_CASE2', 'Case 2: WAREHOUSE 단독'),
      subtitle: t('LBL_SUB_CASE2', '@Transactional("warehouseTm")'),
      value: case2Name,
      setValue: setCase2Name,
      result: case2Result
    },
    {
      id: 'case3IndependentCommit',
      title: t('LBL_CASE3', 'Case 3: 개별 커밋'),
      subtitle: t('LBL_SUB_CASE3', '독립 트랜잭션'),
      value: case3Name,
      setValue: setCase3Name,
      result: case3Result
    },
    {
      id: 'case4CrossDb',
      title: t('LBL_CASE4', 'Case 4: 원자적 트랜잭션'),
      subtitle: t('LBL_SUB_CASE4', 'ChainedTransactionManager'),
      value: case4Name,
      setValue: setCase4Name,
      result: case4Result
    }
  ]
  const setters: Record<string, (r: Result) => void> = {
    case1PrimaryOnly: setCase1Result,
    case2WarehouseOnly: setCase2Result,
    case3IndependentCommit: setCase3Result,
    case4CrossDb: setCase4Result
  }

  // 카드 flex-fill 공통 스타일 (body 가 남은 높이 채우고 스크롤)
  const fillCardStyle = { display: 'flex', flexDirection: 'column' as const, minHeight: 0, flex: 1 }
  const fillCardBodyStyle = {
    body: {
      flex: 1,
      minHeight: 0,
      padding: 8,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column' as const
    }
  }

  return (
    <PageContainer>
      <Flex justify="space-between" align="center" style={{ flexShrink: 0 }}>
        <h3 style={{ margin: 0 }}>Multi-DB Transaction Sample</h3>
        <Button icon={<ReloadOutlined />} onClick={fn_refresh} loading={loading}>
          Refresh
        </Button>
      </Flex>

      {/* 좌: 두 DB 테이블 상하 / 우: 4 Case 카드 2x2 — 전체 flex fill */}
      <Flex gap={12} align="stretch" style={{ flex: 1, minHeight: 0 }}>
        {/* 좌측 컬럼 — PRIMARY Customers (상) + WAREHOUSE Inventory (하) */}
        <Flex vertical gap={12} style={{ flex: 1, minHeight: 0 }}>
          <Card
            size="small"
            title="PRIMARY DB — Customers"
            style={fillCardStyle}
            styles={fillCardBodyStyle}
          >
            <DataTable<Customer>
              rowKey="customerId"
              dataSource={customersDs.getVisibleRows().map((r) => r.data)}
              columns={[
                { title: 'ID', dataIndex: 'customerId', width: 60 },
                { title: 'Name', dataIndex: 'customerName' },
                { title: 'Type', dataIndex: 'customerType', width: 80 }
              ]}
            />
          </Card>
          <Card
            size="small"
            title="WAREHOUSE DB — Inventory"
            style={fillCardStyle}
            styles={fillCardBodyStyle}
          >
            <DataTable<Inventory>
              rowKey="inventoryId"
              dataSource={inventoryDs.getVisibleRows().map((r) => r.data)}
              columns={[
                { title: 'ID', dataIndex: 'inventoryId', width: 60 },
                { title: 'Name', dataIndex: 'itemName' },
                { title: 'WH', dataIndex: 'warehouseCd', width: 60 },
                { title: 'Qty', dataIndex: 'quantity', width: 80 }
              ]}
            />
          </Card>
        </Flex>

        {/* 우측 컬럼 — 4 Case 카드 2x2 grid, 각 카드 flex fill */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: '1fr 1fr',
            gap: 12
          }}
        >
          {cases.map((c) => (
            <Card
              key={c.id}
              size="small"
              title={c.title}
              extra={<span style={{ fontSize: 12, color: '#888' }}>{c.subtitle}</span>}
              style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
              styles={{
                body: {
                  flex: 1,
                  minHeight: 0,
                  padding: 12,
                  overflow: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8
                }
              }}
            >
              <Input
                value={c.value}
                onChange={(e) => c.setValue(e.target.value)}
                placeholder="name"
                style={{ flexShrink: 0 }}
              />
              <Space style={{ flexShrink: 0 }}>
                <Button
                  type="primary"
                  loading={loading}
                  onClick={() => runCase(c.id, c.value, false, setters[c.id])}
                >
                  Commit
                </Button>
                <Button
                  danger
                  loading={loading}
                  onClick={() => runCase(c.id, c.value, true, setters[c.id])}
                >
                  Rollback
                </Button>
              </Space>
              {c.result && (
                <Alert
                  type={c.result.ok ? 'success' : 'error'}
                  message={c.result.msg}
                  showIcon
                  style={{ flexShrink: 0 }}
                />
              )}
            </Card>
          ))}
        </div>
      </Flex>
    </PageContainer>
  )
}
